import { useState, useEffect, useMemo } from 'react';
import { Card, LoadingSpinner } from '../components/Layout';
import api, { CompanyEvent } from '../services/api';

interface CalendarEvent {
  id: string;
  title: string;
  date: Date;
  endDate?: Date;
  type: 'shift' | 'holiday' | 'team_meeting' | 'training' | 'production' | 'announcement' | 'other';
  allDay: boolean;
  startTime?: string;
  endTime?: string;
  location?: string;
  description?: string;
  color?: string;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: CalendarEvent[];
}

const eventTypeColors: Record<string, string> = {
  shift: 'bg-blue-600',
  holiday: 'bg-red-600',
  team_meeting: 'bg-purple-600',
  training: 'bg-green-600',
  production: 'bg-orange-600',
  announcement: 'bg-yellow-600',
  other: 'bg-gray-600',
};

const eventTypeLabels: Record<string, string> = {
  shift: 'My Shift',
  holiday: 'Holiday',
  team_meeting: 'Meeting',
  training: 'Training',
  production: 'Production',
  announcement: 'Announcement',
  other: 'Event',
};

export function SchedulePage() {
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');

  // Filters
  const [filters, setFilters] = useState({
    shifts: true,
    holidays: true,
    events: true,
  });

  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  useEffect(() => {
    loadCalendarData();
  }, [currentMonth, currentYear]);

  const loadCalendarData = async () => {
    try {
      setLoading(true);
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();

      const firstDay = new Date(year, currentDate.getMonth(), 1);
      const lastDay = new Date(year, currentDate.getMonth() + 1, 0);
      const startDate = firstDay.toISOString().split('T')[0];
      const endDate = lastDay.toISOString().split('T')[0];

      const [shifts, companyEvents, holidays] = await Promise.all([
        api.getMyShifts(month, year).catch(() => []),
        api.getEmployeeEvents(startDate, endDate).catch(() => []),
        api.getHolidays(year).catch(() => []),
      ]);

      const calendarEvents: CalendarEvent[] = [];

      if (Array.isArray(shifts)) {
        shifts.forEach((shift: any) => {
          calendarEvents.push({
            id: `shift-${shift.id}`,
            title: 'My Shift',
            date: new Date(shift.shift_date || shift.shiftDate),
            type: 'shift',
            allDay: false,
            startTime: shift.start_time || shift.startTime,
            endTime: shift.end_time || shift.endTime,
          });
        });
      }

      if (Array.isArray(companyEvents)) {
        companyEvents.forEach((event: CompanyEvent) => {
          const eventType = event.eventType as CalendarEvent['type'];
          calendarEvents.push({
            id: event.id,
            title: event.title,
            date: new Date(event.startTime),
            endDate: event.endTime ? new Date(event.endTime) : undefined,
            type: eventTypeColors[eventType] ? eventType : 'other',
            allDay: event.allDay,
            startTime: event.allDay ? undefined : new Date(event.startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
            endTime: event.endTime && !event.allDay ? new Date(event.endTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : undefined,
            location: event.location || undefined,
            description: event.description || undefined,
            color: event.color || undefined,
          });
        });
      }

      if (Array.isArray(holidays)) {
        holidays.forEach((holiday: CompanyEvent) => {
          if (calendarEvents.some(e => e.id === holiday.id)) return;
          calendarEvents.push({
            id: holiday.id,
            title: holiday.title,
            date: new Date(holiday.startTime),
            type: 'holiday',
            allDay: true,
            description: holiday.description || undefined,
          });
        });
      }

      setEvents(calendarEvents);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter events
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      if (event.type === 'shift' && !filters.shifts) return false;
      if (event.type === 'holiday' && !filters.holidays) return false;
      if (!['shift', 'holiday'].includes(event.type) && !filters.events) return false;
      return true;
    });
  }, [events, filters]);

  // Generate calendar days for month view
  const calendarDays = useMemo(() => {
    const days: CalendarDay[] = [];
    const firstDay = new Date(currentYear, currentDate.getMonth(), 1);
    const lastDay = new Date(currentYear, currentDate.getMonth() + 1, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Previous month padding
    const firstDayOfWeek = firstDay.getDay();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(firstDay);
      date.setDate(date.getDate() - i - 1);
      days.push({ date, isCurrentMonth: false, isToday: false, events: [] });
    }

    // Current month days
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(currentYear, currentDate.getMonth(), d);
      const dateStr = date.toISOString().split('T')[0];

      const dayEvents = filteredEvents.filter(event => {
        if (!event.date) return false;
        const eventDate = new Date(event.date);
        if (isNaN(eventDate.getTime())) return false;
        return eventDate.toISOString().split('T')[0] === dateStr;
      });

      days.push({
        date,
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime(),
        events: dayEvents,
      });
    }

    // Next month padding
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(lastDay);
      date.setDate(date.getDate() + i);
      days.push({ date, isCurrentMonth: false, isToday: false, events: [] });
    }

    return days;
  }, [currentDate, filteredEvents, currentYear]);

  // Week view days
  const weekDays = useMemo(() => {
    const days: CalendarDay[] = [];
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];

      const dayEvents = filteredEvents.filter(event => {
        if (!event.date) return false;
        const eventDate = new Date(event.date);
        if (isNaN(eventDate.getTime())) return false;
        return eventDate.toISOString().split('T')[0] === dateStr;
      });

      days.push({
        date,
        isCurrentMonth: date.getMonth() === currentDate.getMonth(),
        isToday: date.getTime() === today.getTime(),
        events: dayEvents,
      });
    }
    return days;
  }, [currentDate, filteredEvents]);

  // Day view events
  const dayEvents = useMemo(() => {
    const dateStr = currentDate.toISOString().split('T')[0];
    return filteredEvents
      .filter(event => {
        if (!event.date) return false;
        const eventDate = new Date(event.date);
        if (isNaN(eventDate.getTime())) return false;
        return eventDate.toISOString().split('T')[0] === dateStr;
      })
      .sort((a, b) => {
        if (!a.startTime && !b.startTime) return 0;
        if (!a.startTime) return 1;
        if (!b.startTime) return -1;
        return a.startTime.localeCompare(b.startTime);
      });
  }, [currentDate, filteredEvents]);

  // Navigation
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') newDate.setMonth(newDate.getMonth() - 1);
    else if (viewMode === 'week') newDate.setDate(newDate.getDate() - 7);
    else newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
    setSelectedDay(null);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') newDate.setMonth(newDate.getMonth() + 1);
    else if (viewMode === 'week') newDate.setDate(newDate.getDate() + 7);
    else newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
    setSelectedDay(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDay(null);
  };

  const getEventColor = (event: CalendarEvent) => {
    if (event.color) return '';
    return eventTypeColors[event.type] || 'bg-gray-600';
  };

  // Upcoming events
  const upcomingEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return filteredEvents
      .filter(event => {
        const eventDate = new Date(event.date);
        return !isNaN(eventDate.getTime()) && eventDate >= today;
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, 5);
  }, [filteredEvents]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  if (loading) {
    return <div className="flex justify-center p-8"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Calendar Card */}
      <Card>
        {/* Header with Nav + View Toggle */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">
            {viewMode === 'month' && `${monthNames[currentDate.getMonth()]} ${currentYear}`}
            {viewMode === 'week' && (() => {
              const start = weekDays[0]?.date;
              const end = weekDays[6]?.date;
              if (!start || !end) return '';
              return `${monthNames[start.getMonth()]} ${start.getDate()} - ${end.getMonth() !== start.getMonth() ? monthNames[end.getMonth()] + ' ' : ''}${end.getDate()}`;
            })()}
            {viewMode === 'day' && currentDate.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric' })}
          </h2>
          <div className="flex items-center gap-1">
            {/* View Toggle */}
            <div className="flex items-center bg-gray-700 rounded overflow-hidden mr-2">
              {(['month', 'week', 'day'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-2 py-1 text-xs font-medium transition-colors ${
                    viewMode === mode ? 'bg-yellow-400 text-gray-900' : 'text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>
            <button onClick={goToToday} className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors">
              Today
            </button>
            <button onClick={goToPrevious} className="p-1.5 hover:bg-gray-700 rounded transition-colors">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button onClick={goToNext} className="p-1.5 hover:bg-gray-700 rounded transition-colors">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-4 pb-3 border-b border-gray-700">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.shifts}
              onChange={(e) => setFilters({ ...filters, shifts: e.target.checked })}
              className="w-4 h-4 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
            />
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />
              <span className="text-sm text-gray-300">Shifts</span>
            </div>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.holidays}
              onChange={(e) => setFilters({ ...filters, holidays: e.target.checked })}
              className="w-4 h-4 rounded border-gray-600 text-red-500 focus:ring-red-500"
            />
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-600" />
              <span className="text-sm text-gray-300">Holidays</span>
            </div>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.events}
              onChange={(e) => setFilters({ ...filters, events: e.target.checked })}
              className="w-4 h-4 rounded border-gray-600 text-purple-500 focus:ring-purple-500"
            />
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-purple-600" />
              <span className="text-sm text-gray-300">Events</span>
            </div>
          </label>
        </div>

        {/* MONTH VIEW */}
        {viewMode === 'month' && (
          <>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => (
                <button
                  key={index}
                  onClick={() => day.isCurrentMonth && setSelectedDay(day)}
                  className={`min-h-[60px] p-1 border rounded text-left transition-colors ${
                    day.isCurrentMonth ? 'border-gray-700 bg-gray-800/50 hover:bg-gray-700/50' : 'border-gray-800/50 bg-gray-900/30'
                  } ${day.isToday ? 'ring-2 ring-yellow-400' : ''}`}
                >
                  <div className={`text-xs font-medium mb-0.5 ${day.isCurrentMonth ? 'text-white' : 'text-gray-600'}`}>
                    {day.date.getDate()}
                  </div>
                  <div className="space-y-0.5">
                    {day.events.slice(0, 2).map((event, i) => (
                      <div
                        key={i}
                        className={`text-[10px] px-1 py-0.5 rounded truncate text-white ${getEventColor(event)}`}
                        style={event.color ? { backgroundColor: event.color } : {}}
                      >
                        {event.title}
                      </div>
                    ))}
                    {day.events.length > 2 && (
                      <div className="text-[10px] text-gray-400 text-center">+{day.events.length - 2}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* WEEK VIEW */}
        {viewMode === 'week' && (
          <>
            <div className="grid grid-cols-7 gap-2 mb-2">
              {weekDays.map((day, index) => (
                <div key={index} className="text-center">
                  <div className="text-xs font-medium text-gray-500">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][index]}
                  </div>
                  <div className={`text-lg font-semibold mt-0.5 ${day.isToday ? 'text-yellow-400' : 'text-white'}`}>
                    {day.date.getDate()}
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {weekDays.map((day, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedDay(day)}
                  className={`min-h-[200px] p-2 border rounded text-left transition-colors ${
                    day.isToday ? 'border-yellow-400 bg-gray-800/50' : 'border-gray-700 bg-gray-800/30'
                  } hover:bg-gray-700/50`}
                >
                  <div className="space-y-1.5">
                    {day.events.map((event, i) => (
                      <div
                        key={i}
                        className={`text-xs px-1.5 py-1 rounded text-white ${getEventColor(event)}`}
                        style={event.color ? { backgroundColor: event.color } : {}}
                      >
                        <div className="font-medium truncate">{event.startTime || 'All day'}</div>
                        <div className="truncate">{event.title}</div>
                      </div>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* DAY VIEW */}
        {viewMode === 'day' && (
          <div className="space-y-3">
            {dayEvents.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400">No events scheduled for this day</p>
              </div>
            ) : (
              dayEvents.map((event) => (
                <div
                  key={event.id}
                  className="p-3 bg-gray-700/50 border border-gray-600 rounded-lg"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="text-white font-medium flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${getEventColor(event)}`}
                          style={event.color ? { backgroundColor: event.color } : {}} />
                        {event.title}
                      </div>
                      <div className="text-sm text-gray-400 mt-1">{eventTypeLabels[event.type]}</div>
                      {event.location && (
                        <div className="text-sm text-gray-500 mt-1">
                          <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          {event.location}
                        </div>
                      )}
                      {event.description && (
                        <div className="text-sm text-gray-500 mt-2">{event.description}</div>
                      )}
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded text-white ${getEventColor(event)}`}
                      style={event.color ? { backgroundColor: event.color } : {}}>
                      {event.allDay ? 'All day' : event.startTime || ''}
                      {event.endTime ? ` - ${event.endTime}` : ''}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </Card>

      {/* Stats + Upcoming Side Panel */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* This Month Stats */}
        <Card>
          <h3 className="text-sm font-semibold text-white mb-3">This Month</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-400 text-sm">Shifts</span>
              <span className="text-white font-medium text-sm">
                {events.filter(e => e.type === 'shift').length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400 text-sm">Holidays</span>
              <span className="text-white font-medium text-sm">
                {events.filter(e => e.type === 'holiday').length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400 text-sm">Other Events</span>
              <span className="text-white font-medium text-sm">
                {events.filter(e => !['shift', 'holiday'].includes(e.type)).length}
              </span>
            </div>
          </div>
        </Card>

        {/* Upcoming Events */}
        <Card>
          <h3 className="text-sm font-semibold text-white mb-3">Upcoming</h3>
          {upcomingEvents.length === 0 ? (
            <p className="text-gray-500 text-sm">No upcoming events</p>
          ) : (
            <div className="space-y-2">
              {upcomingEvents.map(event => (
                <button
                  key={event.id}
                  onClick={() => {
                    setCurrentDate(new Date(event.date));
                    setViewMode('day');
                  }}
                  className="w-full flex items-center gap-2 p-2 rounded hover:bg-gray-700/50 transition-colors text-left"
                >
                  <div
                    className={`w-1.5 h-8 rounded ${getEventColor(event)}`}
                    style={event.color ? { backgroundColor: event.color } : {}}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{event.title}</div>
                    <div className="text-xs text-gray-400">
                      {event.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      {!event.allDay && event.startTime && ` at ${event.startTime}`}
                    </div>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${getEventColor(event)} text-white`}
                    style={event.color ? { backgroundColor: event.color } : {}}>
                    {eventTypeLabels[event.type]}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Selected Day Modal */}
      {selectedDay && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50" onClick={() => setSelectedDay(null)}>
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">
                {selectedDay.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </h3>
              <button onClick={() => setSelectedDay(null)} className="text-gray-400 hover:text-white p-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {selectedDay.events.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No events for this day</p>
              ) : (
                <div className="space-y-3">
                  {selectedDay.events.map(event => (
                    <div key={event.id} className="p-3 bg-gray-700/50 rounded-lg">
                      <div className="flex justify-between items-start mb-1">
                        <div>
                          <div className="text-white font-medium flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${getEventColor(event)}`}
                              style={event.color ? { backgroundColor: event.color } : {}} />
                            {event.title}
                          </div>
                          <div className="text-sm text-gray-400">{eventTypeLabels[event.type]}</div>
                        </div>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded text-white ${getEventColor(event)}`}
                          style={event.color ? { backgroundColor: event.color } : {}}>
                          {event.allDay ? 'All day' : event.startTime || ''}
                        </span>
                      </div>
                      {event.location && (
                        <div className="text-sm text-gray-500 mt-1">
                          <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          {event.location}
                        </div>
                      )}
                      {event.description && (
                        <div className="text-sm text-gray-500 mt-2 pt-2 border-t border-gray-600">
                          {event.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
